import { Component, OnInit } from '@angular/core';
import { AsyncPipe, Location, NgClass, NgForOf, NgIf } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  FormBuilder,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { PersonApiService } from '../../api/person-api.service';
import { Person } from '../../api/person-api.class';
import { StoreService } from '../../store/store.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import {
  NzDropDownADirective,
  NzDropDownDirective,
  NzDropdownMenuComponent,
} from 'ng-zorro-antd/dropdown';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { FormTextareaComponent } from '../../shared/ui/form-textarea/form-textarea.component';
import { FormSelectComponent } from '../../shared/ui/form-select/form-select.component';
import { FormInputComponent } from '../../shared/ui/form-input/form-input.component';
import { Clothing } from '../../api/clothing-api.class';
import { GetMatchcodesOutput } from '../../api/provision-request-api.types';
import { ClothingApiService } from '../../api/clothing-api.service';
import { GetClothingHistoryOutput } from '../../api/clothing-api.types';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-clothing-detail',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MainButtonComponent,
    NgIf,
    NgClass,
    NgForOf,
    NzIconModule,
    NzAutocompleteModule,
    NzDatePickerModule,
    NzSpinModule,
    NzInputDirective,
    NzInputGroupComponent,
    NzSelectModule,
    NzModalModule,
    NzTimelineModule,
    ReactiveFormsModule,
    NzCollapseModule,
    NzDropdownMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    NzDropDownADirective,
    NzDropDownDirective,
    FormTextareaComponent,
    FormSelectComponent,
    FormInputComponent,
  ],
  templateUrl: './clothing-detail.component.html',
  styleUrl: './clothing-detail.component.scss',
})
export class ClothingDetailComponent implements OnInit {
  private searchSubject = new Subject<string>();
  clothing$: Observable<Clothing>;
  searchedPersons$?: Observable<Person[]>;
  clothingHistory: GetClothingHistoryOutput = [];
  isLoadingHistory = false;
  editMode = false;
  isLoading = false;
  modalIsVisible = false;
  searchTerm?: string;
  date: Date[] = [];
  selectSizes: GetMatchcodesOutput[] = [];
  selectStatus = [
    { valueKey: 1, labelKey: 'Ausgeliehen' },
    { valueKey: 2, labelKey: 'Beschädigt' },
    { valueKey: 3, labelKey: 'Dauerleihe' },
    { valueKey: 4, labelKey: 'Frei' },
    { valueKey: 5, labelKey: 'Zum Waschen' },
  ];
  selectGender = [
    { valueKey: 1, labelKey: 'Männlich' },
    { valueKey: 2, labelKey: 'Weiblich' },
    { valueKey: 3, labelKey: 'Divers' },
  ];

  validateLockerForm = this.fb.group({
    optionalFields: this.fb.group({
      bemerkung: this.fb.control<string | null>(null),
      person: this.fb.control<string | null>(null),
      firma: this.fb.control<string | null>(null),
    }),
    mandatoryFields: this.nonNullableFb.group({
      bezeichnung: this.nonNullableFb.control('', [Validators.required]),
      kleidergroesse: this.nonNullableFb.control(0, [Validators.required]),
      status: this.nonNullableFb.control(0, [Validators.required]),
      geschlecht: this.nonNullableFb.control(0, [Validators.required]),
      inventar: this.nonNullableFb.control('', [Validators.required]),
    }),
  });

  constructor(
    private fb: FormBuilder,
    private nonNullableFb: NonNullableFormBuilder,
    private personApiService: PersonApiService,
    private clothingApiService: ClothingApiService,
    private modal: NzModalService,
    private store: StoreService,
    private location: Location,
  ) {
    this.clothing$ = this.store
      .select((state) => state.clothing.activeClothing)
      .pipe(map((c) => new Clothing(c!)));
  }

  async ngOnInit() {
    await this.prefillForm();
    this.searchedPersons$ = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchTerm) => searchTerm.length >= 2),
      switchMap((searchTerm) => {
        this.isLoading = true;
        return this.personApiService.searchPerson({ name: searchTerm }).pipe(
          map((response) => Person.fromResultArray(response)),
          catchError((err) => {
            if (err.status === 404) {
              return of([]);
            } else {
              return throwError(() => err);
            }
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        );
      }),
    );

    this.selectSizes = await this.clothingApiService.getClothingSizes();
  }

  navigateBack() {
    this.location.back();
  }

  async prefillForm() {
    const clothing = await firstValueFrom(this.clothing$);
    this.validateLockerForm.get('mandatoryFields')?.patchValue({
      bezeichnung: clothing.bezeichnung,
      kleidergroesse: clothing.kleidergroesse,
      status: clothing.status,
      geschlecht: clothing.geschlecht,
      inventar: clothing.inventarnummer,
    });

    this.validateLockerForm.get('optionalFields')?.patchValue({
      bemerkung: clothing.bemerkung ?? '',
      person: clothing.person?.vorname + ' ' + clothing.person?.name,
      firma: clothing.firma ?? 'Keine Angabe',
    });
  }

  switchEditMode() {
    this.editMode = !this.editMode;
  }

  onAssignClick() {
    this.modalIsVisible = true;
  }

  handleCancel() {
    this.modalIsVisible = false;
    this.searchTerm = '';
  }

  onInput() {
    if (this.searchTerm) this.searchSubject.next(this.searchTerm);
  }

  async tagAsDamaged() {
    const clothing = await firstValueFrom(this.clothing$);
    await this.clothingApiService.damageClothing(clothing.id);
    const {
      clothing: { setActiveClothing },
    } = this.store.getActions();

    await setActiveClothing(clothing.id);
    await this.prefillForm();
    await this.getClothingHistory(true);
  }

  async tagForLaundering() {
    const clothing = await firstValueFrom(this.clothing$);
    await this.clothingApiService.launderClothing(clothing.id);
    const {
      clothing: { setActiveClothing },
    } = this.store.getActions();

    await setActiveClothing(clothing.id);
    await this.prefillForm();
    await this.getClothingHistory(true);
  }

  async withdrawClothing() {
    const clothing = await firstValueFrom(this.clothing$);
    await this.clothingApiService.withdrawClothing(clothing.id);

    const {
      clothing: { setActiveClothing },
    } = this.store.getActions();

    await setActiveClothing(clothing.id);
    await this.prefillForm();
    await this.getClothingHistory(true);
  }

  async assignClothing() {
    const clothing = await firstValueFrom(this.clothing$);
    await this.clothingApiService.assignClothing({
      kleidungsstuecke: [clothing.id],
      person_id: parseInt(this.searchTerm!),
    });

    const {
      clothing: { setActiveClothing },
    } = this.store.getActions();

    await setActiveClothing(clothing.id);
    await this.prefillForm();
    this.modalIsVisible = false;
    await this.getClothingHistory(true);
  }

  onDeleteClothingClick() {
    this.modal.confirm({
      nzTitle: 'Bestätigung der Löschung',
      nzContent:
        '<b style="color: red;">Sind Sie sicher, dass Sie dieses ' +
        'Kleidungsstück löschen möchten?</b>',
      nzOkText: 'Ja',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: async () => {
        await this.deleteClothing();
        this.navigateBack();
      },
      nzCancelText: 'Nein',
      nzOnCancel: () => console.log('Cancel'),
    });
  }

  async deleteClothing() {
    const clothing = await firstValueFrom(this.clothing$);
    await this.clothingApiService.deleteClothing(clothing.id);
  }

  formatDate(date: string) {
    return DateTime.fromISO(date).toFormat('D', { locale: 'de' });
  }

  setHistoryColor(event: number | undefined) {
    switch (event) {
      case (event = 1):
        return 'green';
      case (event = 2):
        return 'red';
      default:
        return 'blue';
    }
  }

  async getClothingHistory(event: boolean) {
    if (event) {
      this.isLoadingHistory = true;
      const clothing = await firstValueFrom(this.clothing$);
      this.clothingHistory = await this.clothingApiService.getClothingHistory(
        clothing.id,
      );
      this.clothingHistory.reverse();
      this.isLoadingHistory = false;
    }
  }

  getPersonGroup(person: any): string {
    return person.gruppe ? person.gruppe : 'Keine';
  }
}
